import React, { useState, useEffect } from "react";
import { X, Phone, Mail, Save, Loader2, Calendar, Wallet, Clock, Tag, MessageSquare } from "lucide-react";
import { toast } from "sonner";
import { ADMIN } from "@/constants/testIds";
import { adminApi, STATUS_OPTIONS, statusMeta } from "./auth";

const fmt = (v) => {
  try {
    const d = typeof v === "string" ? new Date(v) : v;
    return d.toLocaleString(undefined, {
      month: "short",
      day: "numeric",
      year: "numeric",
      hour: "numeric",
      minute: "2-digit",
    });
  } catch (_e) {
    return String(v);
  }
};

const LeadDrawer = ({ lead, onClose, onUpdated }) => {
  const [status, setStatus] = useState(lead?.status || "new");
  const [notes, setNotes] = useState(lead?.notes || "");
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    setStatus(lead?.status || "new");
    setNotes(lead?.notes || "");
  }, [lead]);

  if (!lead) return null;
  const meta = statusMeta(status);

  const save = async () => {
    setSaving(true);
    try {
      const { data } = await adminApi.patch(`/admin/quotes/${lead.id}`, { status, notes });
      toast.success("Lead updated");
      onUpdated(data);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Update failed");
    } finally {
      setSaving(false);
    }
  };

  return (
    <div
      data-testid={ADMIN.drawer}
      className="fixed inset-0 z-[60] flex justify-end"
      role="dialog"
      aria-modal="true"
    >
      <button
        aria-label="Close drawer"
        onClick={onClose}
        className="absolute inset-0 bg-[#121212]/60 backdrop-blur-sm"
      />
      <div className="relative w-full sm:w-[520px] md:w-[600px] max-w-full h-full bg-[#FAF9F6] shadow-2xl overflow-y-auto">
        <div className="sticky top-0 z-10 bg-[#1A1A1A] text-[#FAF9F6] px-6 md:px-8 py-5 flex items-center justify-between">
          <div className="min-w-0">
            <p className="text-[10px] tracking-[0.32em] uppercase text-[#C19A6B] font-semibold">
              Lead Details
            </p>
            <h3 className="font-serif-display text-2xl md:text-3xl mt-1 truncate">{lead.name}</h3>
          </div>
          <button
            data-testid={ADMIN.drawerClose}
            onClick={onClose}
            aria-label="Close"
            className="w-10 h-10 border border-white/20 hover:bg-[#C19A6B] hover:text-[#1A1A1A] hover:border-[#C19A6B] transition-colors flex items-center justify-center shrink-0"
          >
            <X className="w-4 h-4" />
          </button>
        </div>

        <div className="p-6 md:p-8 space-y-8">
          {/* Contact */}
          <section>
            <p className="text-[10px] tracking-[0.32em] uppercase text-[#5C4033] font-semibold mb-3">
              Contact
            </p>
            <div className="grid sm:grid-cols-2 gap-3">
              <a
                href={`tel:${lead.phone}`}
                data-testid={ADMIN.drawerCallLink}
                className="flex items-center gap-3 border border-[#1A1A1A]/15 px-4 py-3 hover:border-[#5C4033] transition-colors group"
              >
                <span className="w-9 h-9 bg-[#5C4033] text-[#FAF9F6] flex items-center justify-center shrink-0">
                  <Phone className="w-4 h-4" />
                </span>
                <div className="min-w-0">
                  <p className="text-[10px] tracking-[0.28em] uppercase text-[#1A1A1A]/55">Call</p>
                  <p className="text-sm text-[#1A1A1A] group-hover:text-[#5C4033] truncate">
                    {lead.phone}
                  </p>
                </div>
              </a>
              <a
                href={`mailto:${lead.email}`}
                data-testid={ADMIN.drawerEmailLink}
                className="flex items-center gap-3 border border-[#1A1A1A]/15 px-4 py-3 hover:border-[#5C4033] transition-colors group"
              >
                <span className="w-9 h-9 bg-[#5C4033] text-[#FAF9F6] flex items-center justify-center shrink-0">
                  <Mail className="w-4 h-4" />
                </span>
                <div className="min-w-0">
                  <p className="text-[10px] tracking-[0.28em] uppercase text-[#1A1A1A]/55">Email</p>
                  <p className="text-sm text-[#1A1A1A] group-hover:text-[#5C4033] truncate">
                    {lead.email}
                  </p>
                </div>
              </a>
            </div>
          </section>

          {/* Project details */}
          <section>
            <p className="text-[10px] tracking-[0.32em] uppercase text-[#5C4033] font-semibold mb-3">
              Project
            </p>
            <div className="grid sm:grid-cols-2 gap-4">
              <Field icon={<Tag className="w-3.5 h-3.5" />} label="Type" value={lead.project_type} />
              <Field icon={<Wallet className="w-3.5 h-3.5" />} label="Budget" value={lead.budget_range} />
              <Field icon={<Clock className="w-3.5 h-3.5" />} label="Timeline" value={lead.timeline} />
              <Field
                icon={<Calendar className="w-3.5 h-3.5" />}
                label="Submitted"
                value={fmt(lead.created_at)}
              />
            </div>
            <div className="mt-5">
              <p className="text-[10px] tracking-[0.28em] uppercase text-[#1A1A1A]/55 mb-2 flex items-center gap-2">
                <MessageSquare className="w-3.5 h-3.5" /> Details from client
              </p>
              <p className="text-[#1A1A1A]/85 leading-relaxed whitespace-pre-wrap border-l-2 border-[#C19A6B] pl-4 bg-[#F1ECE0]/60 p-4">
                {lead.project_details}
              </p>
            </div>
            {lead.inspiration_image_url && (
              <div className="mt-4">
                <p className="text-[10px] tracking-[0.28em] uppercase text-[#1A1A1A]/55 mb-2">
                  Inspiration Image
                </p>
                <img
                  src={lead.inspiration_image_url}
                  alt="Client inspiration"
                  className="w-full max-h-72 object-cover border border-[#1A1A1A]/10"
                />
              </div>
            )}
          </section>

          {/* Status */}
          <section>
            <p className="text-[10px] tracking-[0.32em] uppercase text-[#5C4033] font-semibold mb-3">
              Pipeline Status
            </p>
            <div className="grid grid-cols-2 sm:grid-cols-3 gap-2">
              {STATUS_OPTIONS.map((opt) => (
                <button
                  key={opt.value}
                  type="button"
                  data-testid={`${ADMIN.drawerStatus}-${opt.value}`}
                  onClick={() => setStatus(opt.value)}
                  className={`px-3 py-2.5 text-[10px] tracking-[0.24em] uppercase font-semibold border transition-all ${
                    status === opt.value
                      ? `${opt.color} border-transparent`
                      : "bg-transparent border-[#1A1A1A]/20 text-[#1A1A1A]/70 hover:border-[#1A1A1A]"
                  }`}
                >
                  {opt.label}
                </button>
              ))}
            </div>
            <p className="mt-3 text-xs text-[#1A1A1A]/50">
              Current: <span className={`inline-block px-2 py-0.5 text-[10px] tracking-widest ${meta.color}`}>{meta.label}</span>
            </p>
          </section>

          {/* Notes */}
          <section>
            <p className="text-[10px] tracking-[0.32em] uppercase text-[#5C4033] font-semibold mb-3">
              Internal Notes
            </p>
            <textarea
              data-testid={ADMIN.drawerNotes}
              value={notes}
              onChange={(e) => setNotes(e.target.value)}
              rows={5}
              placeholder="Private notes for your team — the client won't see this."
              className="w-full bg-[#F1ECE0] border border-[#1A1A1A]/15 p-4 text-sm text-[#1A1A1A] focus:outline-none focus:border-[#5C4033] transition-colors resize-y"
            />
          </section>

          <div className="pt-4 border-t border-[#1A1A1A]/10 sticky bottom-0 bg-[#FAF9F6] -mx-6 md:-mx-8 px-6 md:px-8 pb-6">
            <button
              data-testid={ADMIN.drawerSave}
              onClick={save}
              disabled={saving}
              className="btn-walnut w-full justify-center disabled:opacity-60"
            >
              {saving ? (
                <>
                  <Loader2 className="w-4 h-4 animate-spin" />
                  Saving...
                </>
              ) : (
                <>
                  <Save className="w-4 h-4" />
                  Save Changes
                </>
              )}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

const Field = ({ icon, label, value }) => (
  <div className="border border-[#1A1A1A]/10 p-4 bg-[#FAF9F6]">
    <p className="text-[10px] tracking-[0.28em] uppercase text-[#1A1A1A]/55 flex items-center gap-2">
      {icon}
      {label}
    </p>
    <p className="mt-1.5 text-[#1A1A1A] text-sm">{value || "—"}</p>
  </div>
);

export default LeadDrawer;
